import { api, scopeId } from '../api.ts';
import type { Session } from '../api.ts';
import { badge, section } from '../components.ts';
import { duration, empty, h, when } from '../ui.ts';

interface JournalRow {
  at: number;
  event: string;
  payload: Record<string, unknown>;
}
type Full = Session & { journal: JournalRow[] };

/** One session: what was heard, attributed, what she said, and what she wrote from it. Updates live while open. */
export async function sessionView(id: string): Promise<HTMLElement> {
  const s = await api.get<Full>(`/sessions/${id}`);
  const root = h('div', { class: 'page' });
  root.append(...header(s, id));
  const transcript = transcriptSection(s);
  const said = h('div', { class: 'stack' }, empty('Address her by name in the meeting, or ask from the project page.'));
  root.append(
    section('Transcript', h('div', { class: 'card' }, transcript)),
    section('She said', said),
    section('Written from this session', writtenSection(s)),
  );
  if (!s.endedAt) follow(id, root, transcript, said);
  return root;
}

function header(s: Full, id: string): HTMLElement[] {
  const status = s.endedAt ? badge('outline', 'ended') : badge('success', h('span', { class: 'dot pulse' }), 'live');
  const out = [
    h('a', { class: 'back', href: '#/knowledge' }, '‹ Knowledge'),
    h('h1', { class: 'page-title' }, s.title),
    h(
      'p',
      { class: 'page-sub row' },
      status,
      ` ${s.kind} · `,
      h('a', { class: 'link', href: `#/project/${scopeId(s.scope)}` }, scopeId(s.scope)),
      ` · started ${when(s.startedAt)} · ${duration(s.startedAt, s.endedAt)}${s.live ? ` · Live ${s.live.seconds}s` : ''}`,
    ),
  ];
  if (!s.endedAt) {
    const end = async () => {
      await api.post(`/sessions/${id}/end`);
      location.reload();
    };
    out.push(
      h(
        'div',
        { class: 'row' },
        h('button', { class: 'btn destructive', type: 'button', onclick: () => void end() }, 'End session'),
      ),
    );
  }
  return out;
}

function transcriptSection(s: Full): HTMLElement {
  const transcript = h('div', { class: 'transcript' });
  const segs = s.journal.filter((j) => j.event === 'transcript.segment');
  if (!segs.length)
    transcript.append(
      empty(s.endedAt ? 'Nothing was transcribed.' : 'Listening… lines appear here a few seconds after they are said.'),
    );
  for (const j of segs) transcript.append(line(j.payload as { speaker: string; text: string }));
  return transcript;
}

function writtenSection(s: Full): HTMLElement {
  const written = h('div');
  const writes = s.journal.filter((j) => j.event === 'note.updated');
  const commits = [...new Set(writes.map((j) => String((j.payload as { commit: string | null }).commit)))].filter(
    (c) => c !== 'null',
  );
  if (!writes.length)
    written.append(empty('Nothing written yet. Files are updated every few segments and when the session ends.'));
  const scope = scopeId(s.scope);
  for (const c of commits)
    written.append(
      h(
        'p',
        { class: 'mono' },
        h('a', { class: 'link', href: `#/project/${scope}/history/${c}` }, c),
        ' ',
        h('span', { class: 'muted' }, 'show diff'),
      ),
    );
  return written;
}

/** Live updates for an open session: new lines, her answers, and a reload when files are written. */
function follow(id: string, root: HTMLElement, transcript: HTMLElement, said: HTMLElement) {
  const off = api.events((name, payload) => {
    const p = payload as { sessionId?: string; text?: string; channel?: string };
    if (p.sessionId !== id) return;
    if (name === 'transcript.segment') {
      transcript.querySelector('.empty')?.remove();
      transcript.append(line(payload as { speaker: string; text: string }));
    }
    if (name === 'channel.message') {
      said.querySelector('.empty')?.remove();
      said.append(
        h(
          'div',
          { class: 'card' },
          h('div', {}, p.text ?? ''),
          h('div', { class: 'mono muted' }, `${p.channel} · ${new Date().toLocaleTimeString()}`),
        ),
      );
    }
    if (name === 'note.updated' || name === 'session.ended') void refresh();
  });
  const refresh = async () => {
    off();
    root.replaceWith(await sessionView(id));
  };
  window.addEventListener('hashchange', off, { once: true });
}

const line = (p: { speaker: string; text: string }) =>
  h('div', { class: 'line' }, h('span', { class: `who ${p.speaker}` }, p.speaker), h('span', {}, p.text));
